import { NamedProperty, CodedProperty } from "sprint-common-web";

import { AncillaryContentSubtype } from "../../../../common/types/AncillaryContentType";
import { BuzzContentReference } from "../../../../common/types/BuzzContentReference";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { ContentSource } from "../../../../common/types/ContentSource";
import { MovieContentSubtype } from "../../../../common/types/MovieContentSubtype";
import { TvSeriesContentSubtype } from "../../../../common/types/TvSeriesContentSubtype";

export type ContentAssociationDetails =
    | ContentAssociationMovieDetails
    | ContentAssociationTvSeriesDetails
    | ContentAssociationAncillaryDetails;

/* movie */
export interface ContentAssociationMovieDetails {
    type: ContentReferenceType.Movie;
    subtype: MovieContentSubtype;
    source: ContentSource;
    id: string;
    title: string;
    buzz?: { id: string; isArchived: boolean; isDeleted: boolean };
    versions: ContentAssociationMovieVersionDetails[];
}

export interface ContentAssociationMovieVersionDetails {
    id: string;
    buzzVersion?: {
        id: string;
        name: string;
        notes?: string;
        isArchived: boolean;
    };
    shreqVersion?: {
        id: string;
        name: string;
    };
    type: NamedProperty;
    brandings?: CodedProperty[];
    distributor: NamedProperty;
    mergedTargetId?: string;
}

export interface ContentAssociationTvSeriesDetails {
    type: ContentReferenceType.TvSeries;
    subtype: TvSeriesContentSubtype;
    source: ContentSource;
    id: string;
    title: string;
    buzzId?: string;
    orphaned: boolean;
    seasons: ContentAssociationTvSeasonDetails[];
    mergedTargetId?: string;
}

export interface ContentAssociationTvSeasonDetails {
    id: string;
    name: string;
    number: number;
    buzzId?: string;
    episodes: ContentAssociationTvEpisodeDetails[];
    mergedTargetId?: string;
}

export interface ContentAssociationTvEpisodeDetails {
    id: string;
    title: string;
    number: number;
    buzz?: { id: string; isArchived: boolean; isDeleted: boolean };
    versions: ContentAssociationTvEpisodeVersionDetails[];
    mergedTargetId?: string;
}

export interface ContentAssociationAncillaryDetails {
    type: ContentReferenceType.Ancillary;
    subtype: AncillaryContentSubtype;
    source: ContentSource;
    id: string;
    title: string;
    buzz?: BuzzContentReference;
    version: ContentAssociationAncillaryVersionDetails;
    isMerged: boolean;
}

export interface ContentAssociationAncillaryVersionDetails {
    id: string;
    type: NamedProperty;
    airlines: CodedProperty[];
    distributor: NamedProperty;
    buzzId?: string;
}

export interface ContentAssociationTvEpisodeVersionDetails {
    id: string;
    buzzVersion?: {
        id: string;
        name: string;
        notes?: string;
        isArchived: boolean;
    };
    mergedTargetId?: string;
    type: NamedProperty;
    distributor: NamedProperty;
    brandings?: CodedProperty[];
}

export const ensureMovieAssociateDetails: (
    details: ContentAssociationDetails
) => asserts details is ContentAssociationMovieDetails = (details: ContentAssociationDetails) => {
    if (details.type !== ContentReferenceType.Movie) {
        throw Error(`Details type should be Movie'`);
    }
};

export const ensureTvAssociateDetails: (
    details: ContentAssociationDetails
) => asserts details is ContentAssociationTvSeriesDetails = (details: ContentAssociationDetails) => {
    if (details.type !== ContentReferenceType.TvSeries) {
        throw Error(`Details type should be Tv Series'`);
    }
};
export const ensureAncillaryDetails: (
    details: ContentAssociationDetails
) => asserts details is ContentAssociationAncillaryDetails = (details: ContentAssociationDetails) => {
    if (details.type !== ContentReferenceType.Ancillary) {
        throw Error(`Details type should be Ancillary'`);
    }
};
